import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const GiveawayCard = ({ giveaway, onParticipate, isLoggedIn, isFeatured = false, viewMode = 'grid' }) => {
  const {
    title,
    description,
    sponsor,
    sponsorLogo,
    coverImage,
    prizeValue,
    category,
    entryMethod,
    timeRemaining,
    participants,
    requirements,
    eligibility,
    featured
  } = giveaway;

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'tech':
        return 'Smartphone';
      case 'lifestyle':
        return 'Coffee';
      case 'experiences':
        return 'Camera';
      default:
        return 'Gift';
    }
  };

  const getEntryMethodIcon = (method) => {
    switch (method) {
      case 'code':
        return 'Hash';
      case 'social':
        return 'Share';
      case 'purchase':
        return 'ShoppingCart';
      default:
        return 'Hash';
    }
  };

  const getTimeColor = (timeRemaining) => {
    const days = parseInt(timeRemaining);
    if (days <= 3) return 'text-error';
    if (days <= 7) return 'text-warning';
    return 'text-success';
  };

  if (viewMode === 'list') {
    return (
      <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 p-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Cover Image */}
          <div className="relative md:w-64 md:flex-shrink-0">
            <img
              src={coverImage}
              alt={title}
              className="w-full h-48 md:h-32 object-cover rounded-xl"
            />
            {(isFeatured || featured) && (
              <div className="absolute top-3 left-3 bg-primary text-white px-2 py-1 rounded-lg text-xs font-medium">
                Featured
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <img
                    src={sponsorLogo}
                    alt={sponsor}
                    className="w-6 h-6 rounded-full object-cover"
                  />
                  <span className="text-sm font-medium text-accent-600">{sponsor}</span>
                </div>

                <h3 className="font-heading font-semibold text-lg text-accent-800 mb-2">
                  {title}
                </h3>

                <p className="text-accent-600 text-sm mb-3 line-clamp-2">
                  {description}
                </p>

                <div className="flex items-center gap-4 text-sm text-accent-600">
                  <div className="flex items-center gap-1">
                    <Icon name={getCategoryIcon(category)} size={16} />
                    <span className="capitalize">{category}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name={getEntryMethodIcon(entryMethod)} size={16} />
                    <span className="capitalize">{entryMethod} entry</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name="Users" size={16} />
                    <span>{participants.toLocaleString()} entries</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-end gap-3">
                <div className="text-right">
                  <div className="text-2xl font-bold text-primary mb-1">
                    {prizeValue}
                  </div>
                  <div className={`text-sm font-medium ${getTimeColor(timeRemaining)}`}>
                    {timeRemaining} left
                  </div>
                </div>

                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => onParticipate()}
                  disabled={!isLoggedIn}
                  iconName="Gift"
                  iconPosition="left"
                  className="shadow-warm"
                >
                  {isLoggedIn ? 'Enter Now' : 'Login to Enter'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden group">
      {/* Cover Image */}
      <div className="relative">
        <img
          src={coverImage}
          alt={title}
          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
        />
        {(isFeatured || featured) && (
          <div className="absolute top-3 left-3 bg-primary text-white px-2 py-1 rounded-lg text-xs font-medium">
            Featured
          </div>
        )}
        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-lg px-2 py-1">
          <span className={`text-sm font-medium ${getTimeColor(timeRemaining)}`}>
            {timeRemaining} left
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Sponsor */}
        <div className="flex items-center gap-2 mb-3">
          <img
            src={sponsorLogo}
            alt={sponsor}
            className="w-6 h-6 rounded-full object-cover"
          />
          <span className="text-sm font-medium text-accent-600">{sponsor}</span>
        </div>

        {/* Title */}
        <h3 className="font-heading font-semibold text-lg text-accent-800 mb-2 line-clamp-2">
          {title}
        </h3>

        {/* Description */}
        <p className="text-accent-600 text-sm mb-4 line-clamp-3">
          {description}
        </p>

        {/* Prize Value */}
        <div className="text-center mb-4">
          <div className="text-2xl font-bold text-primary mb-1">
            {prizeValue}
          </div>
          <div className="text-sm text-accent-600">
            Prize Value
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between text-sm text-accent-600 mb-4">
          <div className="flex items-center gap-1">
            <Icon name={getCategoryIcon(category)} size={16} />
            <span className="capitalize">{category}</span>
          </div>
          <div className="flex items-center gap-1">
            <Icon name={getEntryMethodIcon(entryMethod)} size={16} />
            <span className="capitalize">{entryMethod}</span>
          </div>
          <div className="flex items-center gap-1">
            <Icon name="Users" size={16} />
            <span>{participants.toLocaleString()}</span>
          </div>
        </div>

        {/* Requirements */}
        <div className="bg-surface rounded-lg p-3 mb-4">
          <div className="text-xs font-medium text-accent-700 mb-2">Entry Requirements:</div>
          <ul className="text-xs text-accent-600 space-y-1">
            {requirements?.map((req, index) => (
              <li key={index} className="flex items-center gap-1">
                <Icon name="Check" size={12} className="text-success" />
                {req}
              </li>
            ))}
          </ul>
        </div>

        {/* Action Button */}
        <Button
          variant="primary"
          fullWidth
          onClick={() => onParticipate()}
          disabled={!isLoggedIn}
          iconName="Gift"
          iconPosition="left"
          className="shadow-warm"
        >
          {isLoggedIn ? 'Enter Giveaway' : 'Login to Enter'}
        </Button>

        {/* Eligibility */}
        <div className="mt-3 text-xs text-accent-500 text-center">
          {eligibility}
        </div>
      </div>
    </div>
  );
};

export default GiveawayCard;